import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {
    fetchOperators,
    fetchOperatorById,
    createOperator,
    updateOperator,
    setOperatorPassword,
    deleteOperator,
    fetchSupervisors,
    createSupervisor,
    setSupervisorPassword,
    deleteSupervisor
} from './operators-api';

/* ---------------------- LIST ---------------------- */
export const useOperators = (page = 0, pageSize = 10, filters = {}) =>
    useQuery({
        queryKey: ['operators', page, pageSize, filters],
        queryFn: () => fetchOperators(page, pageSize, filters),
        keepPreviousData: true,
    });

/* ---------------------- SINGLE ---------------------- */
export const useOperator = (id) =>
    useQuery({
        queryKey: ['operator', id],
        queryFn: () => fetchOperatorById(id),
        enabled: !!id,
    });

/* ---------------------- CREATE ---------------------- */
export const useCreateOperator = (onSuccess) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createOperator,
        onSuccess: (data) => {
            queryClient.invalidateQueries({queryKey: ['operators']});
            onSuccess?.(data);
        },
    });
};

/* ---------------------- SET PASSWORD (admin) ---------------------- */
/*
 * No queryClient.invalidateQueries here on purpose: a password is not part of any cached view, and
 * refetching the operator list after a reset would only suggest that something visible changed.
 */
export const useSetOperatorPassword = (onSuccess) =>
    useMutation({
        mutationFn: ({id, newPassword}) => setOperatorPassword(id, newPassword),
        onSuccess: (data) => onSuccess?.(data),
    });

/* ---------------------- UPDATE ---------------------- */
export const useUpdateOperator = (onSuccess) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateOperator,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({queryKey: ['operators']});
            queryClient.setQueryData(['operator', variables.id], data);
            onSuccess?.(data);
        },
    });
};

/* ---------------------- DELETE ---------------------- */
export const useDeleteOperator = (onSuccess) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteOperator,
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({queryKey: ['operators']});
            queryClient.removeQueries({queryKey: ['operator', id]});
            onSuccess?.();
        },
    });
};

/* ---------------------- SUPERVISORS ---------------------- */
export const useSupervisors = () =>
    useQuery({queryKey: ['supervisors'], queryFn: fetchSupervisors});

export const useCreateSupervisor = (onSuccess) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createSupervisor,
        onSuccess: (data) => {
            queryClient.invalidateQueries({queryKey: ['supervisors']});
            onSuccess?.(data);
        },
    });
};

export const useSetSupervisorPassword = (onSuccess) =>
    useMutation({
        mutationFn: ({id, newPassword}) => setSupervisorPassword(id, newPassword),
        onSuccess: (data) => onSuccess?.(data),
    });

export const useDeleteSupervisor = (onSuccess) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteSupervisor,
        onSuccess: (data) => {
            queryClient.invalidateQueries({queryKey: ['supervisors']});
            onSuccess?.(data);
        },
    });
};
